import {
  VStack,
  Stack,
  Hidden,
  Box,
  View,
  // TODO:  added to move to next line and remove eslint error
} from "native-base";
import dayjs from "dayjs";
import FormContainer from "atoms/FormContainer";
import { AiOutlineUser } from "react-icons/ai";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import TextField from "@mui/material/TextField";
import FormMenu from "atoms/FormMenu";
import ValidatedInput from "atoms/ValidatedInput";
import { GENDER_OPTIONS } from "global/constants";
import AmplitudeHelper from "utils/analytics";
import { useTranslation } from "react-i18next";
import InfoBox from "atoms/InfoBox";
import { formatDateToISOWithUTCOffset } from "utils/helpers";

function renderEmpDOB(formikErrors, errors, hasPendingUpdateRequest, values, setFieldValue) {
  return (
    <ValidatedInput
      errors={{ ...formikErrors, ...errors }}
      title="employeeCreation.single.birthDate"
      label="birthDate"
      disabled={hasPendingUpdateRequest}
      component={
        <View testID="viEmpDOB">
          <Hidden till="lg">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                disabled={hasPendingUpdateRequest}
                disableFuture
                views={["year", "month", "day"]}
                value={values?.birthDate ? dayjs(new Date(values?.birthDate)) : ""}
                onChange={(x) => {
                  const selectedDate = new Date(x);
                  AmplitudeHelper.logEvent("date_of_birth_filled", {
                    source: "single_employee_creation_page",
                    input: formatDateToISOWithUTCOffset(selectedDate),
                  });
                  setFieldValue("birthDate", Date.parse(dayjs(x)));
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    error: Object.prototype.hasOwnProperty.call({ ...formikErrors, ...errors }, "birthDate"),
                  },
                }}
                renderInput={(params) => <TextField {...params} helperText={null} />}
              />
            </LocalizationProvider>
          </Hidden>
          <Hidden from="lg">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                disabled={hasPendingUpdateRequest}
                disableFuture
                views={["year", "month", "day"]}
                value={dayjs(values?.birthDate ? new Date(values?.birthDate) : "")}
                disableOpenPicker={hasPendingUpdateRequest}
                onChange={(x) => {
                  const selectedDate = new Date(x);
                  AmplitudeHelper.logEvent("date_of_birth_filled", {
                    source: "single_employee_creation_page",
                    input: formatDateToISOWithUTCOffset(selectedDate),
                  });
                  setFieldValue("birthDate", Date.parse(dayjs(x)));
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    error: Object.prototype.hasOwnProperty.call({ ...formikErrors, ...errors }, "birthDate"),
                  },
                }}
                renderInput={(params) => <TextField {...params} helperText={null} />}
              />
            </LocalizationProvider>
          </Hidden>
        </View>
      }
    />
  );
}

function renderEmpKtp(formikErrors, errors, hasPendingUpdateRequest, values, handleChange) {
  return (
    <ValidatedInput
      testID="viEmpKtp"
      label="ktp"
      errors={{ ...formikErrors, ...errors }}
      title="employeeCreation.single.ktp"
      required
      disabled={hasPendingUpdateRequest}
      value={values?.ktp ?? ""}
      setValue={handleChange("ktp")}
      amplitude="ktp"
    />
  );
}

function renderEmpGender(formikErrors, errors, hasPendingUpdateRequest, values, t, handleChange) {
  return (
    <ValidatedInput
      label="gender"
      errors={{ ...formikErrors, ...errors }}
      disabled={hasPendingUpdateRequest}
      title="employeeCreation.single.gender"
      component={
        <FormMenu
          testID="viEmpGender"
          data={GENDER_OPTIONS}
          isDisabled={hasPendingUpdateRequest}
          value={values?.gender ? t(`home.list.rows.${values?.gender}`) : ""}
          setValue={(x) => handleChange("gender")(x.data)}
          isError={Object.prototype.hasOwnProperty.call(errors, "gender")}
          amplitude="gender"
        />
      }
      required
    />
  );
}

function renderEmpPhone(formikErrors, errors, hasPendingUpdateRequest, values, handleChange) {
  return (
    <ValidatedInput
      testID="viEmpPhone"
      label="phone"
      errors={{ ...formikErrors, ...errors }}
      title="employeeCreation.single.phone"
      allowOnlyNumbers
      required
      disabled={hasPendingUpdateRequest}
      value={values?.phone ?? ""}
      setValue={handleChange("phone")}
      amplitude="phone_number"
    />
  );
}

function renderEmpFullName(formikErrors, errors, hasPendingUpdateRequest, values, handleChange) {
  return (
    <ValidatedInput
      testID="viEmpName"
      label="name"
      errors={{ ...formikErrors, ...errors }}
      title="employeeCreation.single.fullName"
      required
      disabled={hasPendingUpdateRequest}
      value={values?.name}
      setValue={handleChange("name")}
      amplitude="employee_name"
    />
  );
}

const PersonalUserDetails = ({ formikProps = {}, errors = null, hasPendingUpdateRequest = false }) => {
  const { t } = useTranslation("common");
  const direction = ["column", "column", "row", "row", "row"];
  const flex = [null, null, null, 0.5, 0.5];
  const space = [5, 5, 5, 10, 10];

  const { handleChange = () => {}, values = {}, errors: formikErrors = {}, setFieldValue = () => {} } = formikProps;

  return (
    <FormContainer
      title="employeeCreation.single.personalData"
      description="employeeCreation.single.personalDataDesc"
      icon={<AiOutlineUser size={25} />}
    >
      <Stack direction={direction} space={space}>
        <VStack flex={flex} space={3} minW="50%">
          {renderEmpFullName(formikErrors, errors, hasPendingUpdateRequest, values, handleChange)}
          {renderEmpPhone(formikErrors, errors, hasPendingUpdateRequest, values, handleChange)}
          {renderEmpGender(formikErrors, errors, hasPendingUpdateRequest, values, t, handleChange)}
        </VStack>
        <VStack flex={0.5} space={3} minW="45%">
          {renderEmpDOB(formikErrors, errors, hasPendingUpdateRequest, values, setFieldValue)}
          {renderEmpKtp(formikErrors, errors, hasPendingUpdateRequest, values, handleChange)}
        </VStack>
      </Stack>
      {hasPendingUpdateRequest && (
        <Box mt="32px">
          <InfoBox
            warning
            shouldShowDescription
            title="employeeUpdation.single.warningTitle"
            description="employeeUpdation.single.WarningDescription"
          />
        </Box>
      )}
    </FormContainer>
  );
};

export default PersonalUserDetails;
